import { useEffect, useState } from 'react';
import { FaUsers, FaEye, FaTimes } from 'react-icons/fa';

/**
 * Floating Visitor Counter - Displays near chatbot window
 * Shows real-time active users currently on the website
 */
export default function FloatingVisitorCounter() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [displayStats, setDisplayStats] = useState({ users: 0 });
    const [isMinimized, setIsMinimized] = useState(true);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        fetchVisitorStats();

        // Auto-refresh every 15 minutes for real-time data
        const interval = setInterval(() => {
            fetchVisitorStats();
        }, 900000);

        // Check if user previously minimized it
        const minimized = localStorage.getItem('visitorCounterMinimized');
        if (minimized === 'true') {
            setIsMinimized(true);
        }

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (stats !== null) {
            animateCounter(stats);
        }
    }, [stats]);

    const fetchVisitorStats = async () => {
        try {
            const response = await fetch('/api/analytics/realtime');
            const result = await response.json();

            if (result.ok && result.activeUsers !== undefined) {
                setStats(result.activeUsers);
            }
        } catch (error) {
            console.error('Error fetching real-time stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const animateCounter = (targetUsers) => {
        const duration = 1000;
        const steps = 30;
        const increment = duration / steps;
        let currentStep = 0;
        const startValue = displayStats.users;

        const timer = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const currentValue = Math.floor(startValue + (targetUsers - startValue) * progress);

            setDisplayStats({ users: currentValue });

            if (currentStep >= steps) {
                setDisplayStats({ users: targetUsers });
                clearInterval(timer);
            }
        }, increment);

        return () => clearInterval(timer);
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return new Intl.NumberFormat('vi-VN').format(num);
    };

    const toggleMinimize = () => {
        const newState = !isMinimized;
        setIsMinimized(newState);
        localStorage.setItem('visitorCounterMinimized', newState.toString());
    };

    const handleClose = () => {
        setIsVisible(false);
        localStorage.setItem('visitorCounterClosed', 'true');
    };

    if (!isVisible || !stats) {
        return null;
    }

    if (loading) {
        return (
            <div className="fixed bottom-24 right-6 z-[9999] bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg shadow-2xl p-3 animate-pulse">
                <div className="flex items-center gap-2">
                    <FaUsers className="text-lg" />
                    <span className="text-xs">Đang tải...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Minimized State */}
            {isMinimized ? (
                <div
                    onClick={toggleMinimize}
                    className="fixed bottom-[110px] right-11 z-[9999] bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full shadow-2xl p-3 cursor-pointer hover:scale-110 transition-transform duration-200 group"
                >
                    <FaUsers className="text-xl group-hover:animate-pulse" />
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                        {formatNumber(displayStats.users)}
                    </div>
                </div>
            ) : (
                /* Expanded State */
                <div className="fixed bottom-24 right-6 z-[9999] bg-gradient-to-tr from-primary to-secondary text-white rounded-xl shadow-2xl overflow-hidden max-w-xs animate-slide-in-right">
                    {/* Header */}
                    <div className="bg-black/20 px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-xs font-semibold">Đang trực tuyến</span>
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={toggleMinimize}
                                className="hover:bg-white/20 rounded p-1 transition-colors"
                                title="Thu gọn"
                            >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                            <button
                                onClick={handleClose}
                                className="hover:bg-white/20 rounded p-1 transition-colors"
                                title="Đóng"
                            >
                                <FaTimes className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    {/* Stats Content */}
                    <div className="p-4 space-y-3">
                        {/* Real-time Active Users */}
                        <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3 hover:bg-white/20 transition-colors">
                            <div className="bg-green-400/30 rounded-full p-2">
                                <FaUsers className="text-xl text-green-100" />
                            </div>
                            <div className="flex-1">
                                <div className="text-xs text-green-100 opacity-80">Người dùng hoạt động</div>
                                <div className="text-2xl font-bold tabular-nums">
                                    {formatNumber(displayStats.users)}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            )}

            <style jsx>{`
                @keyframes slide-in-right {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                .animate-slide-in-right {
                    animation: slide-in-right 0.3s ease-out;
                }
            `}</style>
        </>
    );
}
